import { defineStore } from "pinia"
import { del, get, patch, post, put } from "./backend"

export type Drf400Error = {
  [key: string]: string[]
}

export interface DatabaseItem {
  id: number
}

export type OptionalId<T> = T & { id?: number }
export type AddId<T> = T & DatabaseItem

export interface ApiQueryFilter {
  type: "ge" | "lt" | "contains" | "eq"
  field: string
  term: string | number
}

export interface ApiQuerySort {
  type: "sort"
  direction: "desc" | "asc"
  field: string
}

export type ApiQuery = ApiQueryFilter | ApiQuerySort

export function buildFilter(query: ApiQuery[]) {
  return query.reduce((a, f) => {
    switch (f.type) {
      case "ge":
        return a + `${f.field}=ge(${f.term})&`
      case "lt":
        return a + `${f.field}=lt(${f.term})&`
      case "contains":
        return a + `${f.field}=co(${f.term})&`
      case "sort":
        return a + `${f.field}=sort(${f.direction})&`
      case "eq":
        return a + `${f.field}=${f.term}&`
      default:
        // @ts-ignore in case I don't see the compile error
        throw Error(`Unknown filter type: ${f.type}`)
    }
  }, "")
}

export class ModelViewSet<
  TCreate extends object,
  TDetail extends DatabaseItem = TCreate & DatabaseItem,
  TList extends DatabaseItem = TDetail,
  TUpdate extends DatabaseItem = TCreate & DatabaseItem,
  TListReturn = TList[],
> {
  endpoint: string
  constructor(endpoint: string) {
    if (endpoint.endsWith("/")) {
      throw Error("Endpoint should not end with a slash")
    }
    if (endpoint.startsWith("/")) {
      throw Error("Endpoint should not start with a slash")
    }
    this.endpoint = endpoint
  }

  list(): Promise<TListReturn> {
    return get<TListReturn>(`${this.endpoint}/`)
  }

  search(filter: ApiQuery[]): Promise<TListReturn> {
    return get<TListReturn>(`${this.endpoint}/?${buildFilter(filter)}`)
  }

  create(data: TCreate) {
    return post<TDetail>(`${this.endpoint}/`, data)
  }

  retrieve(id: number | string) {
    return get<TDetail>(`${this.endpoint}/${id}/`)
  }

  update(data: TUpdate) {
    return put<TDetail>(`${this.endpoint}/${data.id}/`, data)
  }

  partialUpdate(data: Partial<TUpdate>) {
    return patch<TDetail>(`${this.endpoint}/${data.id}/`, data)
  }

  delete(id: number | string) {
    return del<TDetail>(`${this.endpoint}/${id}/`)
  }
}

export interface PaginationResponse<TItem> {
  results: TItem[]
  next: string | null
  previous: string | null
  count: number
}

export class PaginatedModelViewSet<
  TCreate extends object,
  TDetail extends DatabaseItem = TCreate & DatabaseItem,
  TList extends DatabaseItem = TDetail,
  TUpdate extends DatabaseItem = TCreate & DatabaseItem,
> extends ModelViewSet<
  TCreate,
  TDetail,
  TList,
  TUpdate,
  PaginationResponse<TList>
> {
  changePage(url: string) {
    return get<PaginationResponse<TList>>(url)
  }
}

export type ApiStoreClient<
  TCreate extends object,
  TDetail extends DatabaseItem = TCreate & DatabaseItem,
  TList extends DatabaseItem = TDetail,
  TUpdate extends DatabaseItem = TCreate & DatabaseItem,
> = ReturnType<
  ReturnType<typeof apiStoreClient<TCreate, TDetail, TList, TUpdate>>
>

export function apiStoreClient<
  TCreate extends object,
  TDetail extends DatabaseItem = TCreate & DatabaseItem,
  TList extends DatabaseItem = TDetail,
  TUpdate extends DatabaseItem = TCreate & DatabaseItem,
>(
  storeId: string,
  apiClient:
    | ModelViewSet<TCreate, TDetail, TList, TUpdate>
    | PaginatedModelViewSet<TCreate, TDetail, TList, TUpdate>,
) {
  // Type guard to check if apiClient is paginated
  function isPaginated(
    client: typeof apiClient,
  ): client is PaginatedModelViewSet<TCreate, TDetail, TList, TUpdate> {
    return "changePage" in client
  }

  return defineStore(storeId, {
    state: () => ({
      data: [] as (TList | TDetail)[],
      next: null as string | null,
      previous: null as string | null,
      count: 0,
    }),
    getters: {
      getById: (state) => {
        return (id: number) => state.data.find((i) => i.id === id)
      },
    },
    actions: {
      updateData(response: TList[] | PaginationResponse<TList>) {
        if ("results" in response) {
          // @ts-ignore Pinia/reactive/generics don't match.
          this.data = response.results
          this.next = response.next
          this.previous = response.previous
          this.count = response.count
        } else {
          // @ts-ignore Pinia/reactive/generics don't match.
          this.data = response
        }
      },
      async list() {
        this.updateData(await apiClient.list())
      },
      async nextPage() {
        if (this.next && isPaginated(apiClient)) {
          this.updateData(await apiClient.changePage(this.next))
        }
      },
      async previousPage() {
        if (this.previous && isPaginated(apiClient)) {
          this.updateData(await apiClient.changePage(this.previous))
        }
      },
      async retrieve(id: number | string) {
        const local = this.data.find((i) => i.id == id)
        if (local) {
          return local
        } else {
          return await apiClient.retrieve(id)
        }
      },
      async create(item: TCreate) {
        const response = await apiClient.create(item)
        // @ts-ignore Pinia/reactive/generics don't match.
        this.data.push(response)
        return response
      },
      async update(item: TUpdate) {
        const response = await apiClient.update(item)
        const index = this.data.findIndex((i) => i.id === item.id)
        // @ts-ignore Pinia/reactive/generics don't match.
        this.data[index] = response
        return response
      },
      async partialUpdate(item: Partial<TUpdate>) {
        const response = await apiClient.partialUpdate(item)
        const index = this.data.findIndex((i) => i.id === item.id)
        // @ts-ignore Pinia/reactive/generics don't match.
        this.data[index] = response
        return response
      },
      async delete(id: number | string) {
        const response = await apiClient.delete(id)
        this.data = this.data.filter((i) => i.id !== id)
        return response
      },
    },
  })
}
